import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getCurrentCurrencySymbol } from '@tcp/core/src/reduxStore/selectors/session.selectors';
import { getCurrencyAttributes } from '@tcp/core/src/reduxStore/selectors/currency.selectors';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { GlobalAccessibilityLabels } from '@tcp/core/src/utils/labels/globalAccessibility.labels';
import { PLPLabels } from '@tcp/core/src/utils/labels/browse/PLP.labels';
import {
  getPriceData,
  getOfferPrice,
  getOfferPriceRangeAccessibilityLabel,
  getOfferPriceAccessibilityLabel,
} from './plpTileHelpers/priceAndWishlistHelper';
import { MerchantTag, OfferPrice, StrikedThroughPrice } from './styles/PriceAndWishList.style';

import plpTileLocators from './plpTileLocators';

const Price = ({
  minListPrice,
  minOfferPrice,
  lowOfferPrice,
  highOfferPrice,
  lowListPrice,
  highListPrice,
  merchantTag,
  primaryBrand,
  currencySymbol,
  currencyAttributes,
  isBundle,
  isHideOriginalPrice,
  isShowPriceRangeEnabled,
  isTileRedesignAB,
}) => {
  const {
    offerPrice,
    listPrice,
    lowestOfferPrice,
    highestOfferPrice,
    lowestListPrice,
    highestListPrice,
    isShowListPrice,
    isShowOfferPriceRange,
    isShowListPriceRange,
  } = useMemo(
    () =>
      getPriceData({
        minListPrice,
        minOfferPrice,
        lowOfferPrice,
        highOfferPrice,
        lowListPrice,
        highListPrice,
        currencySymbol,
        currencyAttributes,
      }),
    [
      minListPrice,
      minOfferPrice,
      lowOfferPrice,
      highOfferPrice,
      lowListPrice,
      highListPrice,
      currencySymbol,
      currencyAttributes,
    ]
  );

  const showOfferPrices = useMemo(
    () =>
      getOfferPrice(
        isShowOfferPriceRange,
        isShowListPriceRange,
        lowestOfferPrice,
        highestOfferPrice,
        lowestListPrice,
        highestListPrice,
        lowOfferPrice
      ),
    [
      isShowOfferPriceRange,
      isShowListPriceRange,
      lowestOfferPrice,
      highestOfferPrice,
      lowestListPrice,
      highestListPrice,
    ]
  );

  const accessibilityLabels = getLabelFromCopyOrFromTranslation(
    'global.accessibility',
    GlobalAccessibilityLabels
  );

  const priceLabel = getLabelFromCopyOrFromTranslation('PLP.PLP_sort_filter.lbl_price', PLPLabels);
  const salePriceLabel = renderLabel(accessibilityLabels.lbl_sale_price, 'lbl_sale_price');
  const originalPriceLabel = renderLabel(
    accessibilityLabels.lbl_original_price,
    'lbl_original_price'
  );
  const toLabel = renderLabel(accessibilityLabels.lbl_to, 'lbl_to');

  const isShowPriceRange = () => {
    return isShowPriceRangeEnabled && (isShowOfferPriceRange || isShowListPriceRange);
  };

  const renderMerchantTag = () => {
    return merchantTag && <MerchantTag>{merchantTag}</MerchantTag>;
  };

  const renderStrikedThroughPrice = (price) => {
    return <StrikedThroughPrice aria-hidden="true">{price}</StrikedThroughPrice>;
  };

  const renderPriceList = () => {
    return (
      <>
        <span className="sr-only">{`${originalPriceLabel}: ${listPrice}`}</span>
        <StrikedThroughPrice aria-hidden="true">{listPrice}</StrikedThroughPrice>
        {renderMerchantTag()}
      </>
    );
  };

  const renderBundlePrice = () => {
    return (
      <>
        <div>
          <span className="sr-only">
            {`${originalPriceLabel}: ${lowestListPrice} ${toLabel} ${highestListPrice}`}
          </span>
          {renderStrikedThroughPrice(lowestListPrice)}
          <span aria-hidden="true">&nbsp;-&nbsp;</span>
          {renderStrikedThroughPrice(highestListPrice)}
        </div>
        {renderMerchantTag()}
      </>
    );
  };

  const shouldRenderListPrice = listPrice !== offerPrice && !isHideOriginalPrice;
  const shouldRenderListPriceRange =
    !isShowListPriceRange && isShowListPrice && !isHideOriginalPrice;

  return !isBundle && !isShowPriceRange() ? (
    <div
      className={`price-list${isTileRedesignAB ? ' tile-redesign' : ''}`}
      data-locator={plpTileLocators.productPrice}
    >
      <OfferPrice brand={primaryBrand}>
        <span className="sr-only">
          {`${getOfferPriceAccessibilityLabel({
            shouldRenderListPrice,
            priceLabel,
            salePriceLabel,
          })} ${offerPrice}`}
        </span>
        <span aria-hidden="true">{offerPrice}</span>
      </OfferPrice>
      {shouldRenderListPrice ? renderPriceList() : <span />}
    </div>
  ) : (
    <div
      className={`price-list bundle-price${isTileRedesignAB ? ' tile-redesign' : ''}`}
      data-locator={plpTileLocators.productPrice}
    >
      <OfferPrice brand={primaryBrand} isBundle>
        <span className="sr-only">
          {getOfferPriceRangeAccessibilityLabel({
            showOfferPrice: showOfferPrices,
            shouldRenderListPriceRange,
            priceLabel,
            salePriceLabel,
            toLabel,
          })}
        </span>
        <span aria-hidden="true">{showOfferPrices}</span>
      </OfferPrice>
      {shouldRenderListPriceRange ? renderBundlePrice() : null}
    </div>
  );
};

Price.propTypes = {
  onSetLastDeletedItemIdAction: PropTypes.func.isRequired,
  minListPrice: PropTypes.number,
  minOfferPrice: PropTypes.number,
  productPartNo: PropTypes.string,
  merchantTag: PropTypes.string,
  primaryBrand: PropTypes.string,
  currencySymbol: PropTypes.string,
  currencyAttributes: PropTypes.shape({}),
  isShowPriceRangeEnabled: PropTypes.bool,
};

Price.defaultProps = {
  minListPrice: 0,
  minOfferPrice: 0,
  productPartNo: '',
  merchantTag: '',
  primaryBrand: 'tcp',
  currencySymbol: '$',
  currencyAttributes: {},
  isShowPriceRangeEnabled: false,
};

const mapStateToProps = (state) => {
  return {
    currencySymbol: getCurrentCurrencySymbol(state),
    currencyAttributes: getCurrencyAttributes(state),
  };
};

export default connect(mapStateToProps)(Price);
export { Price as PriceVanilla };
